import {
  Box,
  Grid,
  Heading,
  VStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Skeleton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
  Button,
  Link,
  Spinner,
  Badge,
} from '@chakra-ui/react'
import RequestorDashboard from '@/pages/RequestorDashboard'
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
} from 'recharts'
import { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import {
  dashboardService,
  HelpdeskDashboardStats,
} from '@/lib/services/dashboardService'
import { useAuthStore } from '@/store/auth'
import { isRequestorRole } from '@/lib/utils/roleUtils'
import { format, parseISO } from 'date-fns'

const WORKFLOW_CARDS: { key: keyof HelpdeskDashboardStats; label: string }[] = [
  { key: 'unresolved', label: 'Unresolved' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'due_today', label: 'Due today' },
  { key: 'unassigned', label: 'Unassigned' },
]

const defaultTicketStats: HelpdeskDashboardStats = {
  unresolved: 0,
  overdue: 0,
  due_today: 0,
  open: 0,
  on_hold: 0,
  unassigned: 0,
  resolved: 0,
  received: 0,
  average_first_response_seconds: 0,
  resolution_within_sla_percent: 0,
  trends_today: [],
  trends_yesterday: [],
  kpi_open: 0,
  kpi_in_progress: 0,
  kpi_pending: 0,
  kpi_on_hold: 0,
  kpi_closed: 0,
  kpi_cancelled: 0,
  ticket_source: { period: 'last_7_days', total_active: 0, sources: [] },
  agent_performance: [],
}

function formatFirstResponseTime(seconds: number): string {
  if (seconds <= 0) return '—'
  const sec = Math.floor(seconds)
  const ms = Math.round((seconds - sec) * 1000)
  if (ms > 0) return `${sec}s ${ms}ms`
  return `${sec}s`
}

export default function DashboardPage() {
  const user = useAuthStore((s) => s.user)
  const isRequestor = isRequestorRole(user)
  const [ticketStats, setTicketStats] = useState<HelpdeskDashboardStats>(defaultTicketStats)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (isRequestor) return
    const fetchDashboardData = async () => {
      try {
        const ticketData = await dashboardService.getTicketStats()
        setTicketStats({
          ...defaultTicketStats,
          ...ticketData,
          trends_today: ticketData.trends_today ?? [],
          trends_yesterday: ticketData.trends_yesterday ?? [],
        })
      } catch (error) {
        console.warn('Ticket stats not available:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchDashboardData()
  }, [isRequestor])

  if (isRequestor) {
    return <RequestorDashboard />
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={6} align="stretch" flex="1" overflowY="auto" minH={0}>
          {/* Ticket / Helpdesk section */}
          <Heading size="sm" color="gray.600" mt={2} mb={1}>
            Key performance indicators (tickets)
          </Heading>
          <Grid
            templateColumns={{
              base: '1fr 1fr',
              sm: 'repeat(3, 1fr)',
              lg: 'repeat(5, 1fr)',
            }}
            gap={3}
            flexShrink={0}
            w="100%"
            minW={0}
          >
            {[
              { key: 'kpi_open' as const, label: 'Open' },
              { key: 'kpi_in_progress' as const, label: 'In Progress' },
              { key: 'kpi_pending' as const, label: 'Pending' },
              { key: 'kpi_closed' as const, label: 'Closed' },
              { key: 'kpi_cancelled' as const, label: 'Cancelled' },
            ].map(({ key, label }) => (
              <Card key={key} bg="white" borderRadius="md" boxShadow="sm">
                <CardBody py={3}>
                  <Stat>
                    <StatLabel fontSize="xs" color="gray.600">
                      {label}
                    </StatLabel>
                    {isLoading ? (
                      <Skeleton height="6" mt={1} width="50%" />
                    ) : (
                      <>
                        <StatNumber fontSize="xl" fontWeight="bold">
                          {(ticketStats[key] ?? 0).toLocaleString()}
                        </StatNumber>
                        <StatHelpText mb={0} fontSize="xs">
                          — vs last period
                        </StatHelpText>
                      </>
                    )}
                  </Stat>
                </CardBody>
              </Card>
            ))}
          </Grid>

          <Grid
            templateColumns={{
              base: '1fr 1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(4, 1fr)',
            }}
            gap={4}
            flexShrink={0}
            w="100%"
            minW={0}
          >
            {WORKFLOW_CARDS.map(({ key, label }) => (
              <Card key={key} bg="white" borderRadius="md" boxShadow="sm">
                <CardBody>
                  <Stat>
                    <StatLabel fontSize="sm" color="blue.700" fontWeight="medium">
                      {label}
                    </StatLabel>
                    {isLoading ? (
                      <Skeleton height="8" mt={2} width="60%" />
                    ) : (
                      <StatNumber fontSize="2xl" fontWeight="bold" color="blue.900">
                        {(ticketStats[key] as number).toLocaleString()}
                      </StatNumber>
                    )}
                  </Stat>
                </CardBody>
              </Card>
            ))}
          </Grid>

          <Heading size="md" mt={4}>
            Analytics (tickets)
          </Heading>
          <Grid
            templateColumns={{
              base: '1fr 1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(4, 1fr)',
            }}
            gap={4}
            flexShrink={0}
            w="100%"
            minW={0}
          >
            <Card bg="white" borderRadius="md" boxShadow="sm">
              <CardBody>
                <Stat>
                  <StatLabel fontSize="sm" color="blue.700" fontWeight="medium">
                    Resolved
                  </StatLabel>
                  {isLoading ? (
                    <Skeleton height="8" mt={2} width="60%" />
                  ) : (
                    <StatNumber fontSize="2xl" fontWeight="bold" color="blue.900">
                      {ticketStats.resolved.toLocaleString()}
                    </StatNumber>
                  )}
                </Stat>
              </CardBody>
            </Card>
            <Card bg="white" borderRadius="md" boxShadow="sm">
              <CardBody>
                <Stat>
                  <StatLabel fontSize="sm" color="blue.700" fontWeight="medium">
                    Received
                  </StatLabel>
                  {isLoading ? (
                    <Skeleton height="8" mt={2} width="60%" />
                  ) : (
                    <StatNumber fontSize="2xl" fontWeight="bold" color="blue.900">
                      {ticketStats.received.toLocaleString()}
                    </StatNumber>
                  )}
                </Stat>
              </CardBody>
            </Card>
            <Card bg="white" borderRadius="md" boxShadow="sm">
              <CardBody>
                <Stat>
                  <StatLabel fontSize="sm" color="blue.700" fontWeight="medium">
                    Average first response time
                  </StatLabel>
                  {isLoading ? (
                    <Skeleton height="8" mt={2} width="60%" />
                  ) : (
                    <StatNumber fontSize="2xl" fontWeight="bold" color="blue.900">
                      {formatFirstResponseTime(ticketStats.average_first_response_seconds)}
                    </StatNumber>
                  )}
                </Stat>
              </CardBody>
            </Card>
            <Card bg="white" borderRadius="md" boxShadow="sm">
              <CardBody>
                <Stat>
                  <StatLabel fontSize="sm" color="blue.700" fontWeight="medium">
                    Resolution within SLA
                  </StatLabel>
                  {isLoading ? (
                    <Skeleton height="8" mt={2} width="60%" />
                  ) : (
                    <StatNumber fontSize="2xl" fontWeight="bold" color="blue.900">
                      {ticketStats.resolution_within_sla_percent.toFixed(2)}%
                    </StatNumber>
                  )}
                </Stat>
              </CardBody>
            </Card>
          </Grid>

          {ticketStats.trends_today.length === 24 && (
            <Card bg="white" borderRadius="md" boxShadow="sm" flexShrink={0} w="100%">
              <CardHeader>
                <Heading size="md">Today&apos;s trends</Heading>
                <Text fontSize="sm" color="gray.500">
                  Created date — hour of the day
                </Text>
              </CardHeader>
              <CardBody>
                <Box h="280px" w="100%">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart
                      data={Array.from({ length: 24 }, (_, i) => ({
                        hour: i,
                        name: `${i}:00`,
                        Today: ticketStats.trends_today[i] ?? 0,
                        Yesterday: ticketStats.trends_yesterday[i] ?? 0,
                      }))}
                      margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="Today"
                        stroke="#3182CE"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        name="Today"
                      />
                      <Line
                        type="monotone"
                        dataKey="Yesterday"
                        stroke="#90CDF4"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        name="Yesterday"
                      />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                </Box>
              </CardBody>
            </Card>
          )}

          <Grid
            templateColumns={{ base: '1fr', lg: '1fr 1.2fr' }}
            gap={4}
            flexShrink={0}
            w="100%"
            minW={0}
          >
            <Card bg="white" borderRadius="md" boxShadow="sm">
              <CardHeader pb={2}>
                <Heading size="md">Ticket source</Heading>
              </CardHeader>
              <CardBody pt={0}>
                {isLoading ? (
                  <Box h="220px" display="flex" alignItems="center" justifyContent="center">
                    <Skeleton height="180px" width="180px" borderRadius="full" />
                  </Box>
                ) : (
                  <Box position="relative" h="220px" display="flex" alignItems="center" justifyContent="center">
                    <Box position="absolute" textAlign="center" zIndex={1}>
                      <Text fontSize="2xl" fontWeight="bold" color="blue.700">
                        {ticketStats.ticket_source?.total_active ?? 0}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        Active tickets
                      </Text>
                    </Box>
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={ticketStats.ticket_source?.sources ?? []}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={2}
                          label={({ name, percent }) =>
                            percent > 0 ? `${name} ${(percent * 100).toFixed(0)}%` : ''
                          }
                        >
                          {(ticketStats.ticket_source?.sources ?? []).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </CardBody>
            </Card>

            <Card bg="white" borderRadius="md" boxShadow="sm">
              <CardHeader pb={2}>
                <Heading size="md">Agent performance (Top 5)</Heading>
              </CardHeader>
              <CardBody pt={0} overflowX="auto">
                {isLoading ? (
                  <Skeleton height="200px" width="100%" />
                ) : (
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Agent</Th>
                        <Th>Team</Th>
                        <Th>Open ticket</Th>
                        <Th>Status</Th>
                        <Th>Avg FRT</Th>
                        <Th>CSAT</Th>
                        <Th>Load</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(ticketStats.agent_performance ?? []).length === 0 ? (
                        <Tr>
                          <Td colSpan={7} textAlign="center" color="gray.500" py={6}>
                            No agent data
                          </Td>
                        </Tr>
                      ) : (
                        (ticketStats.agent_performance ?? []).slice(0, 5).map((agent) => (
                          <Tr key={agent.agent_id}>
                            <Td>
                              <Flex align="center" gap={2}>
                                <Box
                                  w="8"
                                  h="8"
                                  borderRadius="full"
                                  bg="blue.100"
                                  color="blue.700"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  fontSize="xs"
                                  fontWeight="bold"
                                >
                                  {agent.initials}
                                </Box>
                                {agent.name}
                              </Flex>
                            </Td>
                            <Td>{agent.team}</Td>
                            <Td>{agent.open_ticket}</Td>
                            <Td>
                              <Text
                                fontSize="sm"
                                color={
                                  agent.status === 'Top Performer'
                                    ? 'blue.600'
                                    : agent.status === 'At Risk'
                                      ? 'orange.600'
                                      : 'green.600'
                                }
                                fontWeight="medium"
                              >
                                {agent.status}
                              </Text>
                            </Td>
                            <Td>
                              {agent.avg_frt_seconds >= 60
                                ? `${Math.floor(agent.avg_frt_seconds / 60)}m`
                                : `${agent.avg_frt_seconds}s`}
                            </Td>
                            <Td>{agent.csat != null ? agent.csat.toFixed(1) : '—'}</Td>
                            <Td>
                              <Progress
                                value={agent.load_percent}
                                size="sm"
                                colorScheme="blue"
                                borderRadius="full"
                                w="80px"
                              />
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Grid>
        </VStack>
      </Box>
    </AdminLayout>
  )
}

